import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import { selectIsAuth, selectIsDarkMode } from "@shared/slices/common.slice";
import store from "app/store";
import { localStorageService } from "utils/localStorageService";
import {
  EnvironmentType,
  OpenMessageType,
  UserSource,
  UtmParams,
} from "@shared/types";
import {
  getDisplayResultMessageFunction,
  getEnvironmentWebsiteUrl,
  getMarketingWebsiteUrl,
  getNotificationDisplayTime,
  updateLocalStorageUtmData,
} from "@shared/utils/misc";
import message from "antd/es/message";
import React, {
  ComponentType,
  LazyExoticComponent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { getSharedOnboardingUserSource } from "@shared/features/onboarding/utils";
import {
  LOCAL_STORAGE_REGISTRATION_DATA_KEY,
  UNSAVED_CHANGES_WARNING_MESSAGE,
} from "@shared/common/constants";
import { BotSourceValueType } from "analytics/Avo";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils";
import * as Sentry from "@sentry/react";
import { throttle } from "lodash";
import {
  CODE_SPLITTING_REFRESH_QUERY_PARAM,
  INIT_DATA_QUERY_PARAM,
  IS_WEB_VIEW,
  NEXT_URL_QUERY_PARAM,
  PAGE_SPINNER_MARGIN_FROM_TOP,
} from "common/constants";
import QueriesError from "common/Empty/QueriesError.tsx";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay.tsx";
import { requestIntercomAction } from "services/webview/webViewBridgeUtils.ts";
import { IntercomActionType } from "@shared/services/webview/types.ts";
import { show, showArticle, showSpace } from "@intercom/messenger-js-sdk";

export const copyCodeToClipboard = (text: string) => {
  const el = document.createElement("textarea");
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

export const getPairFromSymbol = (symbol: string) => {
  return symbol.split("/")[1];
};

export const openMessage = (
  description: string,
  type?: OpenMessageType,
  duration?: number,
  maxCount?: number
) => {
  const isDarkMode = getIsDarkMode();
  const parsedDescription =
    typeof description === "string"
      ? description.split("\n").map((text, index) => {
          if (index === 0) return <span key={index}>{text}</span>;
          return <div>{text}</div>;
        })
      : description;
  duration =
    duration ||
    (typeof description === "string"
      ? getNotificationDisplayTime(description, true)
      : 8);
  message[type || "success"]({
    content: parsedDescription,
    duration,
    className: isDarkMode ? "dark-mode" : "",
  });
  message.config({ maxCount: maxCount });
};

export const displayResultMessage =
  getDisplayResultMessageFunction(openMessage);

export const confirmWithCallback = (
  confirmModal: any,
  confirmMessage: ReactNode,
  title?: string,
  showIcon = true,
  okText?: string,
  cancelText?: string,
  hideCancel?: boolean
): Promise<boolean> =>
  new Promise((resolve) => {
    confirmModal.confirm({
      title: title || "Confirm",
      icon: showIcon && <ExclamationCircleOutlined />,
      content: confirmMessage,
      okText: okText || "Save",
      cancelText: cancelText || "Cancel",
      cancelButtonProps: hideCancel
        ? { style: { display: "none" } }
        : undefined,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
      centered: true,
    });
  });

export const showUnsavedChangesWarning = (confirmModal: any) =>
  // @ts-ignore
  confirmWithCallback(
    confirmModal,
    UNSAVED_CHANGES_WARNING_MESSAGE,
    "Unsaved changes",
    true,
    "No",
    "Yes"
  );

export const getIsDarkMode = () =>
  !selectIsAuth(store.getState()) && selectIsDarkMode(store.getState());

export const getOnboardingUserSource = () => {
  const registrationData = localStorageService.getValue(
    LOCAL_STORAGE_REGISTRATION_DATA_KEY
  );
  return getSharedOnboardingUserSource(registrationData);
};

export const getAvoBotSource = (): BotSourceValueType | undefined => {
  const userSource = getOnboardingUserSource();
  return userSource === UserSource.DIRECT ? undefined : userSource;
};

export const removeLocalStorageSignalRefKey = () => {
  localStorageService.removeValue(LOCAL_STORAGE_REGISTRATION_DATA_KEY);
};

export const startIntercomChecklist = (checklistId: number) => {
  // @ts-ignore
  window.Intercom?.("startChecklist", checklistId);
};

export const copyImgToClipboard = async (imageUrl?: string) => {
  try {
    if (imageUrl) {
      const copiedImage = await fetch(imageUrl);
      const blobData = await copiedImage.blob();
      const clipboardItemInput = new ClipboardItem({ "image/png": blobData });
      navigator.clipboard
        .write([clipboardItemInput])
        .then(() => openMessage(getCopyMessage("Image"), "info"));
    }
  } catch (e) {
    Sentry.captureException(
      `Encountered error when running copyImgToClipboard in share image modal: ${e}`
    );
  }
};

export const getDownloadImageUrl = async (imageUrl: string) => {
  const response = await fetch(imageUrl);
  const blobData = await response.blob();
  return window.URL.createObjectURL(new Blob([blobData]));
};

export const openLink = (link: string) => window.open(link, "_blank");

export function useIdle(ms = 1000 * 60) {
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    let timeoutId;

    const handleTimeout = () => {
      setIdle(true);
    };

    const handleEvent = throttle(() => {
      setIdle(false);

      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(handleTimeout, ms);
    }, 500);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleEvent();
      }
    };

    timeoutId = window.setTimeout(handleTimeout, ms);

    window.addEventListener("mousemove", handleEvent);
    window.addEventListener("mousedown", handleEvent);
    window.addEventListener("resize", handleEvent);
    window.addEventListener("keydown", handleEvent);
    window.addEventListener("touchstart", handleEvent);
    window.addEventListener("wheel", handleEvent);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("mousemove", handleEvent);
      window.removeEventListener("mousedown", handleEvent);
      window.removeEventListener("resize", handleEvent);
      window.removeEventListener("keydown", handleEvent);
      window.removeEventListener("touchstart", handleEvent);
      window.removeEventListener("wheel", handleEvent);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearTimeout(timeoutId);
    };
  }, [ms]);

  return idle;
}

export const getEnvironmentType = () => {
  if (import.meta.env.PROD) return EnvironmentType.PROD;
  return EnvironmentType.LOCAL;
};

export const getWebsiteUrl = (path: string) => {
  return `${
    import.meta.env.VITE_APP_WEB_BASE_URL ??
    getEnvironmentWebsiteUrl(getEnvironmentType())
  }${path}`;
};

export const redirectWithTelegramWebData = (
  initData: string,
  additionalParams?: Record<string, string>,
  nextPath?: string
) => {
  let parsedInitData = "";
  try {
    parsedInitData = btoa(initData);
  } catch {}
  const query = new URLSearchParams();
  if (parsedInitData) {
    query.set(INIT_DATA_QUERY_PARAM, parsedInitData);
  }
  if (nextPath) {
    query.set(NEXT_URL_QUERY_PARAM, nextPath);
  }
  if (additionalParams) {
    Object.entries(additionalParams).map(([key, value]) =>
      query.set(key, value)
    );
  }
  const websiteUrl = getWebsiteUrl(`?${query.toString()}`);
  (window as any)?.Telegram?.WebApp?.openLink(websiteUrl);
};

const marketingWebsiteUrl = getMarketingWebsiteUrl(getEnvironmentType());
const inIframe = window !== window.parent;
if (inIframe) {
  window.addEventListener("message", function (message) {
    let data: any;
    try {
      data = JSON.parse(message.data);
    } catch {}
    if (
      message.origin === marketingWebsiteUrl &&
      !!data &&
      UtmParams.UTM_SOURCE in data
    ) {
      updateLocalStorageUtmData(data);
    }
  });
}

export const lazyLoad = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): LazyExoticComponent<T> => {
  const retryImport = async () => {
    try {
      return await importer();
    } catch (error) {
      for (let i = 0; i < 4; i++) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        try {
          return await importer();
        } catch {}
      }
      const url = new URL(window.location.href);
      if (!url.searchParams.get(CODE_SPLITTING_REFRESH_QUERY_PARAM)) {
        url.searchParams.append(CODE_SPLITTING_REFRESH_QUERY_PARAM, "1");
        window.location.replace(url.toString());
        return {
          default: () => (
            <LoadingSpinnerOverlay
              isLoading={true}
              spinnerMarginTop={PAGE_SPINNER_MARGIN_FROM_TOP}
              spinnerSize="large"
            />
          ),
        };
      } else {
        Sentry.captureException(
          "Failed to load code splitting file, showing error page."
        );
        return {
          default: (() => <QueriesError isGeneralPageError={true} />) as any,
        };
      }
    }
  };
  return React.lazy(retryImport);
};

export const getParsedData = (data: string) => {
  try {
    return JSON.parse(data);
  } catch {}
};

export const showIntercomSpace = (space: string) => {
  if (IS_WEB_VIEW) {
    requestIntercomAction({
      type: IntercomActionType.SHOW_SPACE,
      itemId: space,
    }).catch(() => {});
  } else {
    showSpace(space);
  }
};

export const showIntercomArticle = (articleId: string) => {
  if (IS_WEB_VIEW) {
    requestIntercomAction({
      type: IntercomActionType.SHOW_ARTICLE,
      itemId: articleId,
    }).catch(() => {});
  } else {
    showArticle(articleId);
  }
};

export const showIntercom = () => {
  if (IS_WEB_VIEW) {
    requestIntercomAction({
      type: IntercomActionType.SHOW,
    }).catch(() => {});
  } else {
    show();
  }
};
